import React from "react";
import EventListener from "react-event-listener";

import algoliasearch from "algoliasearch";
import algoliasearchHelper from "algoliasearch-helper";
import { Provider, connect } from "react-algoliasearch-helper";
import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";
import debounce from "lodash/debounce";

import connectToStores from "alt-utils/lib/connectToStores";
import SearchActions from "../../../actions/SearchActions";
import SearchStore from "../../../stores/SearchStore";
import UserStore from "../../../stores/UserStore";

const TYPESENSE_MODE = window.location.search.indexOf("search_legacy=1") === -1;

class AlgoliaContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      fetching: false,
      connected: false,
      client: null,
      helper: null,
      selectedIndex: -1,
      showResults: false
    };

    this.onWindowClick = this.onWindowClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.setSelectedIndex = this.setSelectedIndex.bind(this);
    this.openHitByIndex = this.openHitByIndex.bind(this);
    this.setShowResults = this.setShowResults.bind(this);
  }

  static getStores() {
    return [SearchStore, UserStore];
  }

  static getPropsFromStores() {
    let state = SearchStore.getState();
    state.isLoggedIn = UserStore.getState().isLoggedIn;
    state.condensedUsers = UserStore.getState().condensedUsers;
    return state;
  }

  static contextTypes = {
    router: React.PropTypes.object
  };

  componentDidMount() {
    this.componentWillReceiveProps(this.props);
  }

  componentWillReceiveProps(props) {
    let {
        isLoggedIn,
        infoFetched,
        godInfoFetched,
        config,
        godConfig,
        godMode
      } = props,
      { fetching, connected } = this.state;

    if (godMode) {
      infoFetched = godInfoFetched;
      config = godConfig;
    }

    if (!isLoggedIn) {
      SearchActions.reset.defer();
      this.setState({ connected: false, fetching: false });
      return;
    }

    if (!infoFetched && !fetching) {
      SearchActions.fetchInfo.defer({
        godMode,
        typesenseMode: TYPESENSE_MODE ? true : undefined
      });
      this.setState({ connected: false, fetching: true });
    }

    if (infoFetched && !connected && config) {
      let client;
      if (config.platform === "typesense") {
        const typesenseInstantsearchAdapter = new TypesenseInstantSearchAdapter(
          {
            server: {
              apiKey: config.public_api_key,
              nodes: config.nodes,
              nearestNode: config.nearest_node || undefined,
              cacheSearchResultsForSeconds: 10
            },
            additionalSearchParameters: {
              queryBy: "name,email,code,codes,content"
            }
          }
        );
        client = typesenseInstantsearchAdapter.searchClient;
        client._builtinSearch = client.search;
        client.search = debounce((queries, cb) => {
          queries.forEach(q => {
            q.params.highlightPreTag = "<em>";
            q.params.highlightPostTag = "</em>";
          });
          client
            ._builtinSearch(queries)
            .then(data => cb(null, data))
            .catch(e => cb(e));
        }, 150);
      } else {
        client = algoliasearch(config.application_id, config.public_api_key);
      }

      let helper = algoliasearchHelper(client, config.index);

      this.setState(
        {
          connected: true,
          fetching: false,
          client: client,
          helper: helper
        },
        () => {
          // Reset selection whenever query changes
          helper.on("search", () => {
            this.setSelectedIndex(0);
            this.setShowResults(true);
          });
        }
      );
    }
  }

  onWindowClick(e) {
    if (!this.container || !this.container.parentNode.contains(e.target)) {
      this.setShowResults(false);
    }
  }

  onKeyDown(e) {
    // Ignore typing
    if (
      e.target.tagName === "INPUT" ||
      e.target.tagName === "SELECT" ||
      e.target.tagName === "TEXTAREA" ||
      e.target.isContentEditable
    ) {
      return;
    }

    // If cc entry is open, ignore so we don't accidentally swallow
    // the auto-injected credit card details into a search.
    if (document.getElementById("CreditCardEntry--input")) {
      return;
    }

    switch (e.which) {
      case 191: // FORWARD SLASH / QUESTION MARK\
        e.stopPropagation();
        e.preventDefault();
        this.setShowResults(true);

      default:
      // fall through
    }
  }

  setSelectedIndex(selectedIndex) {
    // Wrap-around selection
    if (this.state.helper.lastResults) {
      let maxIndex = this.state.helper.lastResults.hits.length - 1;
      if (selectedIndex > maxIndex) {
        selectedIndex = 0;
      }
      if (selectedIndex < 0) {
        selectedIndex = maxIndex;
      }
    }

    this.setState({ selectedIndex });
  }

  setShowResults(showResults) {
    this.setState({ showResults });
  }

  openHitByIndex(index) {
    if (!index) {
      index = this.state.selectedIndex;
    }
    if (
      index < 0 ||
      !this.state.helper.lastResults ||
      index >= this.state.helper.lastResults.hits.length
    ) {
      return;
    }

    let adminUrlPath,
      adminUrlUsedPopulatedId,
      hit = this.state.helper.lastResults.hits[index];

    if (hit._type in this.props.types) {
      adminUrlPath = this.props.types[hit._type].admin_url_path;
      adminUrlUsedPopulatedId =
        this.props.types[hit._type].admin_url_use_populated_id;
    } else {
      adminUrlPath = hit._type;
      adminUrlUsedPopulatedId = false;
    }

    const destination =
      hit._type === "GiftCard"
        ? `/gift_cards?gift_card_id=${hit.objectID}`
        : `${
            hit.event_bucket ? `/event_buckets/${hit.event_bucket}` : ""
          }/${adminUrlPath}/${
            adminUrlUsedPopulatedId ? hit._populated_from : hit.objectID
          }${this.props.godMode ? `?_god_become_user=${hit._owner}` : ""}`;

    if (this.props.godMode) {
      window.open(destination);
      return;
    } else {
      this.context.router.push(destination);
    }

    this.state.helper.setQuery("").search();
    this.setState({ showResults: false });
  }

  render() {
    let { connected, helper, selectedIndex, showResults } = this.state,
      { types, isLoggedIn, godMode, condensedUsers } = this.props;

    if (!connected) {
      return null;
    }

    let childProps = {
      resultTypes: types
    };

    return (
      <Provider helper={helper}>
        <div
          ref={ref => {
            this.container = ref;
          }}
        >
          {!godMode ? (
            <EventListener
              target="window"
              onClick={this.onWindowClick}
              onKeyDown={this.onKeyDown}
            />
          ) : null}
          {this.props.children.map((c, i) =>
            React.cloneElement(c, {
              key: i,
              selectedIndex: selectedIndex,
              showResults: showResults,
              setSelectedIndex: this.setSelectedIndex,
              openHitByIndex: this.openHitByIndex,
              setShowResults: this.setShowResults,
              godMode: godMode,
              condensedUsers: godMode ? condensedUsers : undefined
            })
          )}
        </div>
      </Provider>
    );
  }
}

export default connectToStores(AlgoliaContainer);



// WEBPACK FOOTER //
// ./src/app/components/partials/search/search-container.jsx